import { Table } from '@radix-ui/themes'
import { css, keyframes, styled } from 'styled-components'

const pageFadeIn = keyframes`
  from { opacity: 0.5; }
  to { opacity: 1; }
`

export const GECKO_POWER_TABLE_FLAT_DENSITY = {
  borderSpacing: '0',
  cellPadding: '0.5rem',
  fontSize: '0.875rem',
  headerHeight: '2.5rem',
  lineHeight: '1.25rem',
  rowHeight: '2.25rem',
} as const

/** Grey rows match page background; hover matches embedded sidebar selected item (white/15). */
export const GECKO_POWER_TABLE_ROW_BG = 'var(--gecko-table-row-bg)'
export const GECKO_POWER_TABLE_ROW_HOVER_BG = 'var(--gecko-table-row-hover-bg)'

export const StyledTableRoot = styled(Table.Root)<{
  $cellPadding?: string
  $cellFontSize?: string
  $borderSpacing?: string
}>`
  height: 100%;
  min-height: 0;
  flex: 1 1 auto;
  --gecko-table-row-bg: ${({ theme }) => theme.forecast.pageBackground1};
  --gecko-table-row-hover-bg: color-mix(
    in srgb,
    white 15%,
    ${({ theme }) => theme.forecast.pageBackground1}
  );
  --table-cell-padding: ${({ $cellPadding }) =>
    $cellPadding || GECKO_POWER_TABLE_FLAT_DENSITY.cellPadding};
  --table-cell-font-size: ${({ $cellFontSize }) =>
    $cellFontSize || GECKO_POWER_TABLE_FLAT_DENSITY.fontSize};
  --table-cell-line-height: ${({ $cellFontSize }) =>
    $cellFontSize ? 'normal' : GECKO_POWER_TABLE_FLAT_DENSITY.lineHeight};
  --table-row-height: ${GECKO_POWER_TABLE_FLAT_DENSITY.rowHeight};

  table {
    border-collapse: separate;
    border-spacing: ${({ $borderSpacing }) =>
      $borderSpacing || GECKO_POWER_TABLE_FLAT_DENSITY.borderSpacing};
  }
`

// When maxHeight is provided, we need to make rt-ScrollAreaRoot scrollable
export const StyledTableRootWithMaxHeight = styled(StyledTableRoot)`
  /* Target the rt-ScrollAreaRoot created by Radix Table.Root */
  .rt-ScrollAreaRoot {
    overflow-y: auto;
    overflow-x: auto;
    max-height: var(--table-max-height);
  }
`

export const SyncedHorizontalScrollbar = styled.div<{
  $isPinned?: boolean
  $isVisible: boolean
}>`
  display: ${({ $isVisible }) => ($isVisible ? 'block' : 'none')};
  position: ${({ $isPinned }) => ($isPinned ? 'absolute' : 'relative')};
  ${({ $isPinned }) =>
    $isPinned &&
    css`
      right: 0;
      bottom: 0;
      left: 0;
    `}
  z-index: 4;
  height: 1rem;
  min-height: 1rem;
  padding: 0.25rem 0.5rem;
  border-top: 1px solid var(--border);
  background-color: ${({ theme }) => theme.forecast.pageBackground1};
`

export const SyncedHorizontalScrollbarTrack = styled.div`
  position: relative;
  width: 100%;
  height: 0.5rem;
  border-radius: 999px;
  background-color: color-mix(in srgb, var(--foreground) 12%, transparent);
  cursor: pointer;
`

export const SyncedHorizontalScrollbarThumb = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  min-width: 2rem;
  border-radius: 999px;
  background-color: color-mix(in srgb, var(--foreground) 42%, transparent);
  cursor: grab;
  touch-action: none;

  &:hover {
    background-color: color-mix(in srgb, var(--foreground) 56%, transparent);
  }

  &:focus-visible {
    outline: 2px solid var(--ring);
    outline-offset: 2px;
  }

  &:active {
    cursor: grabbing;
  }
`

export const TableContainer = styled.div<{
  $maxHeight?: string
  $showHorizontalScrollbar?: boolean
}>`
  overflow: ${({ $maxHeight, $showHorizontalScrollbar }) =>
    $showHorizontalScrollbar ? 'hidden' : $maxHeight ? 'visible' : 'auto'};
  box-sizing: border-box;
  flex-grow: 1;
  flex: 1;
  min-height: 0;
  position: relative;
  ${({ $showHorizontalScrollbar }) =>
    $showHorizontalScrollbar &&
    css`
      padding-bottom: 1rem;
    `}
  ${({ $maxHeight }) =>
    $maxHeight ? `--table-max-height: ${$maxHeight};` : ''}
`
export const TopBarContainer = styled.div`
  padding: 0 0.5rem;
`
export const StyledSubheaderSection = styled.div`
  padding-bottom: 0.5rem;
`

interface StyledTableHeaderProps {
  $isTableHeaderSticky?: boolean
}

export const StyledTableHeader = styled(Table.Header)<StyledTableHeaderProps>`
  color: var(--muted-foreground);
  height: ${GECKO_POWER_TABLE_FLAT_DENSITY.headerHeight};
  min-height: ${GECKO_POWER_TABLE_FLAT_DENSITY.headerHeight};
  flex-grow: 0;
  flex-shrink: 0;
  background-color: ${GECKO_POWER_TABLE_ROW_BG};

  tr {
    height: ${GECKO_POWER_TABLE_FLAT_DENSITY.headerHeight};
  }

  th {
    height: ${GECKO_POWER_TABLE_FLAT_DENSITY.headerHeight};
    border-bottom: 1px solid var(--border) !important;
  }

  ${({ $isTableHeaderSticky }) =>
    $isTableHeaderSticky &&
    css`
      position: sticky;
      top: 0;
      z-index: 2;

      /* Ensure header cells are also sticky */
      th {
        position: sticky;
        top: 0;
        z-index: 2;
        background-color: ${GECKO_POWER_TABLE_ROW_BG};
      }
    `}
`

interface StyledTableCellProps {
  $cellPadding?: string
  $cellFontSize?: string
}

export const StyledTableCell = styled(Table.Cell)<StyledTableCellProps>`
  color: var(--foreground);
  padding: ${({ $cellPadding }) =>
    $cellPadding || GECKO_POWER_TABLE_FLAT_DENSITY.cellPadding};
  text-align: left;
  vertical-align: middle;
  box-sizing: border-box;
  height: var(--table-row-height);
  min-height: var(--table-row-height);
  border-style: hidden !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: ${({ $cellFontSize }) =>
    $cellFontSize || GECKO_POWER_TABLE_FLAT_DENSITY.fontSize};
  line-height: var(--table-cell-line-height);
`

export const StyledTableBody = styled(Table.Body)`
  animation: ${pageFadeIn} 150ms ease-out;
`

export const StyledTableRow = styled(Table.Row)<{
  $hasOnClick: boolean
}>`
  background-color: ${GECKO_POWER_TABLE_ROW_BG};
  position: relative;
  height: var(--table-row-height);

  td {
    border-bottom: 1px solid var(--border) !important;
  }

  ${({ $hasOnClick }) =>
    $hasOnClick &&
    css`
      &:hover {
        ${StyledTableCell} {
          background-color: ${GECKO_POWER_TABLE_ROW_HOVER_BG};
        }

        &:hover * {
          cursor: pointer;
        }
      }

      &:active {
        ${StyledTableCell} {
          background-color: ${GECKO_POWER_TABLE_ROW_HOVER_BG};
        }
      }
    `}
`

export const StyledPaginationContainer = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
  display: flex;
  flex-direction: row;
  height: 56px;
  justify-content: space-between;
  align-items: center;
  bottom: 0;
  width: 100%;
  padding: 0 0.25rem;
  flex-grow: 0;
  flex-shrink: 0;

  footer {
    flex-shrink: 0;
  }
`
